import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { Plus, Shield, Edit, Trash2, Calendar, DollarSign, TrendingUp } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { ClimateRisk, ClimateMitigationStrategy } from '@/types/climate';
import { useToast } from '@/hooks/use-toast';

export const MitigationPlanning = () => {
  const [risks, setRisks] = useState<ClimateRisk[]>([]);
  const [strategies, setStrategies] = useState<ClimateMitigationStrategy[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingStrategy, setEditingStrategy] = useState<ClimateMitigationStrategy | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [strategyToDelete, setStrategyToDelete] = useState<ClimateMitigationStrategy | null>(null);
  const { toast } = useToast();

  const [formData, setFormData] = useState<{
    risk_id: string;
    strategy_name: string;
    strategy_type: ClimateMitigationStrategy['strategy_type'];
    strategy_description: string;
    implementation_cost: string;
    annual_operating_cost: string;
    currency: string;
    effectiveness_score: string;
    implementation_timeline: ClimateMitigationStrategy['implementation_timeline'];
    start_date: string;
    target_completion_date: string;
    responsible_owner: string;
    responsible_team: string;
    implementation_status: ClimateMitigationStrategy['implementation_status'];
    progress_percentage: string;
    risk_reduction_potential: string;
    review_frequency: ClimateMitigationStrategy['review_frequency'];
    notes: string;
  }>({
    risk_id: '',
    strategy_name: '',
    strategy_type: 'preventive',
    strategy_description: '',
    implementation_cost: '',
    annual_operating_cost: '',
    currency: 'USD',
    effectiveness_score: '',
    implementation_timeline: 'medium_term',
    start_date: '',
    target_completion_date: '',
    responsible_owner: '',
    responsible_team: '',
    implementation_status: 'planned',
    progress_percentage: '0',
    risk_reduction_potential: '',
    review_frequency: 'quarterly',
    notes: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [risksRes, strategiesRes] = await Promise.all([
        supabase.from('climate_risks' as any).select('*').order('created_at', { ascending: false }),
        supabase.from('climate_mitigation_strategies' as any).select('*').order('created_at', { ascending: false })
      ]);

      setRisks((risksRes.data as unknown as ClimateRisk[]) || []);
      setStrategies((strategiesRes.data as unknown as ClimateMitigationStrategy[]) || []);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No authenticated user');

      const strategyData = {
        ...formData,
        user_id: user.id,
        implementation_cost: formData.implementation_cost ? parseFloat(formData.implementation_cost) : null,
        annual_operating_cost: formData.annual_operating_cost ? parseFloat(formData.annual_operating_cost) : null,
        effectiveness_score: formData.effectiveness_score ? parseFloat(formData.effectiveness_score) : null,
        progress_percentage: parseFloat(formData.progress_percentage),
        risk_reduction_potential: formData.risk_reduction_potential ? parseFloat(formData.risk_reduction_potential) : null,
        risk_id: formData.risk_id || null
      };

      if (editingStrategy) {
        const { error } = await supabase
          .from('climate_mitigation_strategies' as any)
          .update(strategyData)
          .eq('id', editingStrategy.id);

        if (error) throw error;
        toast({
          title: "Success",
          description: "Mitigation strategy updated successfully",
        });
      } else {
        const { error } = await supabase
          .from('climate_mitigation_strategies' as any)
          .insert([strategyData]);

        if (error) throw error;
        toast({
          title: "Success",
          description: "Mitigation strategy created successfully",
        });
      }

      resetForm();
      setIsModalOpen(false);
      loadData();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to save mitigation strategy",
        variant: "destructive",
      });
    }
  };

  const handleEdit = (strategy: ClimateMitigationStrategy) => {
    setEditingStrategy(strategy);
    setFormData({
      risk_id: strategy.risk_id || '',
      strategy_name: strategy.strategy_name,
      strategy_type: strategy.strategy_type,
      strategy_description: strategy.strategy_description || '',
      implementation_cost: strategy.implementation_cost?.toString() || '',
      annual_operating_cost: strategy.annual_operating_cost?.toString() || '',
      currency: strategy.currency,
      effectiveness_score: strategy.effectiveness_score?.toString() || '',
      implementation_timeline: strategy.implementation_timeline || 'medium_term',
      start_date: strategy.start_date || '',
      target_completion_date: strategy.target_completion_date || '',
      responsible_owner: strategy.responsible_owner || '',
      responsible_team: strategy.responsible_team || '',
      implementation_status: strategy.implementation_status,
      progress_percentage: strategy.progress_percentage.toString(),
      risk_reduction_potential: strategy.risk_reduction_potential?.toString() || '',
      review_frequency: strategy.review_frequency,
      notes: strategy.notes || ''
    });
    setIsModalOpen(true);
  };

  const handleDelete = (strategy: ClimateMitigationStrategy) => {
    setStrategyToDelete(strategy);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!strategyToDelete) return;

    try {
      const { error } = await supabase
        .from('climate_mitigation_strategies' as any)
        .delete()
        .eq('id', strategyToDelete.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Mitigation strategy deleted successfully",
      });

      setIsDeleteDialogOpen(false);
      setStrategyToDelete(null);
      loadData();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to delete mitigation strategy",
        variant: "destructive",
      });
      setIsDeleteDialogOpen(false);
      setStrategyToDelete(null);
    }
  };

  const resetForm = () => {
    setFormData({
      risk_id: '',
      strategy_name: '',
      strategy_type: 'preventive',
      strategy_description: '',
      implementation_cost: '',
      annual_operating_cost: '',
      currency: 'USD',
      effectiveness_score: '',
      implementation_timeline: 'medium_term',
      start_date: '',
      target_completion_date: '',
      responsible_owner: '',
      responsible_team: '',
      implementation_status: 'planned',
      progress_percentage: '0',
      risk_reduction_potential: '',
      review_frequency: 'quarterly',
      notes: ''
    });
    setEditingStrategy(null);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'default';
      case 'in_progress': return 'secondary';
      case 'planned': return 'outline';
      case 'cancelled': return 'destructive';
      case 'on_hold': return 'secondary';
      default: return 'outline';
    }
  };

  const getProgressColor = (progress: number) => {
    if (progress >= 90) return 'bg-green-500';
    if (progress >= 60) return 'bg-blue-500';
    if (progress >= 30) return 'bg-yellow-500';
    return 'bg-gray-500';
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading mitigation planning...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Mitigation Planning</h2>
          <p className="text-muted-foreground">Develop and track climate risk mitigation strategies</p>
        </div>

        <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
          <DialogTrigger asChild>
            <Button onClick={resetForm}>
              <Plus className="h-4 w-4 mr-2" />
              Add Strategy
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingStrategy ? 'Edit Mitigation Strategy' : 'Create Mitigation Strategy'}</DialogTitle>
              <DialogDescription>
                Define and track climate risk mitigation measures
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="risk_id">Associated Risk (Optional)</Label>
                  <Select value={formData.risk_id} onValueChange={(value) => setFormData({ ...formData, risk_id: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a risk" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">No specific risk</SelectItem>
                      {risks.map((risk) => (
                        <SelectItem key={risk.id} value={risk.id}>
                          {risk.risk_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="strategy_name">Strategy Name *</Label>
                  <Input
                    id="strategy_name"
                    value={formData.strategy_name}
                    onChange={(e) => setFormData({ ...formData, strategy_name: e.target.value })}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="strategy_type">Strategy Type</Label>
                  <Select value={formData.strategy_type} onValueChange={(value: ClimateMitigationStrategy['strategy_type']) => setFormData({ ...formData, strategy_type: value })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="preventive">Preventive</SelectItem>
                      <SelectItem value="adaptive">Adaptive</SelectItem>
                      <SelectItem value="protective">Protective</SelectItem>
                      <SelectItem value="corrective">Corrective</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="implementation_status">Implementation Status</Label>
                  <Select value={formData.implementation_status} onValueChange={(value: ClimateMitigationStrategy['implementation_status']) => setFormData({ ...formData, implementation_status: value })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="planned">Planned</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                      <SelectItem value="on_hold">On Hold</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="implementation_cost">Implementation Cost</Label>
                  <Input
                    id="implementation_cost"
                    type="number"
                    step="0.01"
                    value={formData.implementation_cost}
                    onChange={(e) => setFormData({ ...formData, implementation_cost: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="annual_operating_cost">Annual Operating Cost</Label>
                  <Input
                    id="annual_operating_cost"
                    type="number"
                    step="0.01"
                    value={formData.annual_operating_cost}
                    onChange={(e) => setFormData({ ...formData, annual_operating_cost: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="effectiveness_score">Effectiveness Score (0-100)</Label>
                  <Input
                    id="effectiveness_score"
                    type="number"
                    min="0"
                    max="100"
                    value={formData.effectiveness_score}
                    onChange={(e) => setFormData({ ...formData, effectiveness_score: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="risk_reduction_potential">Risk Reduction Potential (%)</Label>
                  <Input
                    id="risk_reduction_potential"
                    type="number"
                    min="0"
                    max="100"
                    value={formData.risk_reduction_potential}
                    onChange={(e) => setFormData({ ...formData, risk_reduction_potential: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="implementation_timeline">Implementation Timeline</Label>
                  <Select value={formData.implementation_timeline} onValueChange={(value: ClimateMitigationStrategy['implementation_timeline']) => setFormData({ ...formData, implementation_timeline: value })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="immediate">Immediate</SelectItem>
                      <SelectItem value="short_term">Short Term</SelectItem>
                      <SelectItem value="medium_term">Medium Term</SelectItem>
                      <SelectItem value="long_term">Long Term</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="progress_percentage">Progress Percentage</Label>
                  <Input
                    id="progress_percentage"
                    type="number"
                    min="0"
                    max="100"
                    value={formData.progress_percentage}
                    onChange={(e) => setFormData({ ...formData, progress_percentage: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="start_date">Start Date</Label>
                  <Input
                    id="start_date"
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="target_completion_date">Target Completion Date</Label>
                  <Input
                    id="target_completion_date"
                    type="date"
                    value={formData.target_completion_date}
                    onChange={(e) => setFormData({ ...formData, target_completion_date: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="responsible_owner">Responsible Owner</Label>
                  <Input
                    id="responsible_owner"
                    value={formData.responsible_owner}
                    onChange={(e) => setFormData({ ...formData, responsible_owner: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="responsible_team">Responsible Team</Label>
                  <Input
                    id="responsible_team"
                    value={formData.responsible_team}
                    onChange={(e) => setFormData({ ...formData, responsible_team: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="review_frequency">Review Frequency</Label>
                  <Select value={formData.review_frequency} onValueChange={(value: ClimateMitigationStrategy['review_frequency']) => setFormData({ ...formData, review_frequency: value })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="quarterly">Quarterly</SelectItem>
                      <SelectItem value="semi_annual">Semi-Annual</SelectItem>
                      <SelectItem value="annual">Annual</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="currency">Currency</Label>
                  <Select value={formData.currency} onValueChange={(value) => setFormData({ ...formData, currency: value })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="USD">USD</SelectItem>
                      <SelectItem value="EUR">EUR</SelectItem>
                      <SelectItem value="GBP">GBP</SelectItem>
                      <SelectItem value="CAD">CAD</SelectItem>
                      <SelectItem value="AUD">AUD</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="strategy_description">Strategy Description</Label>
                <Textarea
                  id="strategy_description"
                  value={formData.strategy_description}
                  onChange={(e) => setFormData({ ...formData, strategy_description: e.target.value })}
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                />
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit">
                  {editingStrategy ? 'Update Strategy' : 'Create Strategy'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Strategies</CardTitle>
            <Shield className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{strategies.length}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">In Progress</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {strategies.filter(s => s.implementation_status === 'in_progress').length}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Completed</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {strategies.filter(s => s.implementation_status === 'completed').length}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Investment</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              ${strategies.reduce((sum, s) => sum + (s.implementation_cost || 0), 0).toLocaleString()}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Strategies List */}
      <div className="space-y-4">
        {strategies.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Shield className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-xl font-semibold mb-2">No Mitigation Strategies</h3>
              <p className="text-muted-foreground text-center mb-4">
                Start by creating your first mitigation strategy
              </p>
              <Button onClick={() => setIsModalOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create First Strategy
              </Button>
            </CardContent>
          </Card>
        ) : (
          strategies.map((strategy) => (
            <Card key={strategy.id}>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <CardTitle className="flex items-center gap-2">
                      <Shield className="h-5 w-5" />
                      {strategy.strategy_name}
                    </CardTitle>
                    <CardDescription className="mt-1">
                      {strategy.strategy_type.replace('_', ' ').toUpperCase()} • {strategy.implementation_timeline?.replace('_', ' ')}
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={getStatusColor(strategy.implementation_status)}>
                      {strategy.implementation_status.replace('_', ' ').toUpperCase()}
                    </Badge>
                    <Button variant="outline" size="sm" onClick={() => handleEdit(strategy)}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleDelete(strategy)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {strategy.strategy_description && (
                  <p className="text-sm text-muted-foreground">{strategy.strategy_description}</p>
                )}

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-sm font-medium">Progress</p>
                    <div className="flex items-center gap-2">
                      <Progress value={strategy.progress_percentage} className="flex-1" />
                      <span className="text-sm text-muted-foreground">
                        {strategy.progress_percentage}%
                      </span>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-medium">Risk Reduction</p>
                    <p className="text-sm text-muted-foreground">
                      {strategy.risk_reduction_potential || 0}%
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium">Implementation Cost</p>
                    <p className="text-sm text-muted-foreground">
                      {strategy.implementation_cost
                        ? `${strategy.currency} ${strategy.implementation_cost.toLocaleString()}`
                        : 'Not specified'
                      }
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium">Responsible</p>
                    <p className="text-sm text-muted-foreground">
                      {strategy.responsible_owner || strategy.responsible_team || 'Not assigned'}
                    </p>
                  </div>
                </div>

                {(strategy.start_date || strategy.target_completion_date) && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium">Start Date</p>
                      <p className="text-sm text-muted-foreground">
                        {strategy.start_date ? new Date(strategy.start_date).toLocaleDateString() : 'Not set'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium">Target Completion</p>
                      <p className="text-sm text-muted-foreground">
                        {strategy.target_completion_date ? new Date(strategy.target_completion_date).toLocaleDateString() : 'Not set'}
                      </p>
                    </div>
                  </div>
                )}

                {strategy.risk_id && (
                  <div>
                    <p className="text-sm font-medium">Associated Risk</p>
                    <p className="text-sm text-muted-foreground">
                      {risks.find(r => r.id === strategy.risk_id)?.risk_name || 'Unknown Risk'}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Deletion</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{strategyToDelete?.strategy_name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 mt-4">
            <Button
              variant="outline"
              onClick={() => {
                setIsDeleteDialogOpen(false);
                setStrategyToDelete(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
            >
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
